/** Normalized RGBA baked into `blue_loader.json` stroke (`ty: "st"`). */
const BLUE_LOADER_STROKE: readonly [number, number, number, number] = [
  0.03529411764705882, 0.43137254901960786, 0.9333333333333333, 1,
];

/** Gradient ramp stops in precomp effects (`ADBE Ramp`). */
const RAMP_START: readonly [number, number, number, number] = [
  0.263066768646, 1, 0.867243647575, 1,
];
const RAMP_END: readonly [number, number, number, number] = [
  0.354580283165, 1, 0.820018112659, 1,
];

function vecApproxEq(
  a: readonly number[],
  b: readonly [number, number, number, number],
  eps = 0.002,
): boolean {
  return (
    a.length === 4 &&
    Math.abs(a[0]! - b[0]!) < eps &&
    Math.abs(a[1]! - b[1]!) < eps &&
    Math.abs(a[2]! - b[2]!) < eps &&
    Math.abs(a[3]! - b[3]!) < eps
  );
}

function cssColorToLottieRgba(css: string): [number, number, number, number] {
  const s = css.trim();
  const hexM = s.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
  if (hexM) {
    let h = hexM[1]!;
    if (h.length === 3) {
      h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
    }
    if (h.length === 6) {
      return [
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255,
        1,
      ];
    }
    if (h.length === 8) {
      return [
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255,
        parseInt(h.slice(6, 8), 16) / 255,
      ];
    }
  }
  const rgbM = s.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/,
  );
  if (rgbM) {
    return [
      Number(rgbM[1]) / 255,
      Number(rgbM[2]) / 255,
      Number(rgbM[3]) / 255,
      rgbM[4] != null ? Number(rgbM[4]) : 1,
    ];
  }
  return [0.008, 0.482, 1, 1];
}

function lightenRgba(
  [r, g, b, a]: [number, number, number, number],
  t: number,
): [number, number, number, number] {
  return [r + (1 - r) * t, g + (1 - g) * t, b + (1 - b) * t, a];
}

function recolorStrokes(
  node: unknown,
  target: readonly [number, number, number, number],
  replacement: [number, number, number, number],
): void {
  if (node === null || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const item of node) recolorStrokes(item, target, replacement);
    return;
  }
  const o = node as Record<string, unknown>;
  if (o.ty === "st" && o.c && typeof o.c === "object") {
    const c = o.c as { a?: number; k?: unknown };
    if (
      c.a === 0 &&
      Array.isArray(c.k) &&
      c.k.length === 4 &&
      typeof c.k[0] === "number" &&
      vecApproxEq(c.k as number[], target)
    ) {
      c.k = [...replacement];
    }
  }
  for (const key of Object.keys(o)) {
    recolorStrokes(o[key], target, replacement);
  }
}

/** Static `k` quads used as colors (stroke `c` or effect `v`). */
function recolorStaticQuads(
  node: unknown,
  target: readonly [number, number, number, number],
  replacement: [number, number, number, number],
): void {
  if (node === null || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const item of node) recolorStaticQuads(item, target, replacement);
    return;
  }
  const o = node as Record<string, unknown>;
  if (
    o.a === 0 &&
    Array.isArray(o.k) &&
    o.k.length === 4 &&
    typeof o.k[0] === "number" &&
    vecApproxEq(o.k as number[], target)
  ) {
    o.k = [...replacement];
  }
  for (const key of Object.keys(o)) {
    recolorStaticQuads(o[key], target, replacement);
  }
}

/** Deep-cloned loader JSON with strokes tinted to `primaryCss` (hex/rgb from theme). */
export function getThemedBlueLoader(
  base: Record<string, unknown>,
  primaryCss: string,
): Record<string, unknown> {
  const clone = structuredClone(base) as Record<string, unknown>;
  const rgba = cssColorToLottieRgba(primaryCss);
  recolorStrokes(clone, BLUE_LOADER_STROKE, rgba);
  recolorStaticQuads(clone, RAMP_START, rgba);
  recolorStaticQuads(clone, RAMP_END, lightenRgba(rgba, 0.12));
  return clone;
}
